// 알림 유형 관리 화면 (tb_alert_type)
// isModal: 모달 오버레이로 표시 / isEmbedded: 탭 내부에 삽입 / 기본: 독립 화면
import React, { useState, useEffect, useCallback } from 'react';
import PageLayout from '../../components/PageLayout';
import { fetchAlertTypes, createAlertType, updateAlertType, deleteAlertType } from '../../api/alert';
import './AlertPage.css';
import './AlertTypePage.css';

const CATEGORY_OPTIONS = [
  { value: 'UP',   label: '상승(UP)' },
  { value: 'DOWN', label: '하락(DOWN)' },
  { value: 'BOTH', label: '양방향(BOTH)' },
];
const METRIC_OPTIONS = [
  { value: 'PROFIT_RATE', label: '수익률' },
  { value: 'PRICE',       label: '시세' },
  { value: 'DIFF_RATE',   label: '당일등락률' },
];
const BASE_OPTIONS = [
  { value: 'AUTO',       label: '자동(매수↔매도)' },
  { value: 'BUY_PRICE',  label: '매수단가 기준' },
  { value: 'SELL_PRICE', label: '매도단가 기준' },
];
const COOLDOWN_OPTIONS = [
  { value: 'ONCE',       label: '1회만' },
  { value: 'DAILY_TIME', label: '다음날 지정시간' },
  { value: 'REENTRY',    label: '재진입 시' },
];

const EMPTY_FORM = {
  id: null,
  type_name:       '',
  alert_category:  'UP',
  alert_metric:    'PROFIT_RATE',
  base_price_type: 'AUTO',
  threshold_up:    '',
  threshold_down:  '',
  cooldown_type:   'DAILY_TIME',
  reset_hour:      9,
  reset_minute:    0,
  is_active:       true,
};

const MINUTE_OPTIONS = [0, 10, 20, 30, 40, 50];

const formatResetTime = (hour, minute) =>
  `다음날 ${String(hour ?? 9).padStart(2, '0')}:${String(minute ?? 0).padStart(2, '0')}`;

const catLabel = (v) => CATEGORY_OPTIONS.find(o => o.value === v)?.label || v;
const metLabel = (v) => METRIC_OPTIONS.find(o => o.value === v)?.label  || v;
const cdLabel  = (v) => COOLDOWN_OPTIONS.find(o => o.value === v)?.label || v;

const AlertTypePage = ({ isModal = false, isEmbedded = false, onClose }) => {
  const [types,   setTypes]   = useState([]);
  const [loading, setLoading] = useState(false);
  const [popup,   setPopup]   = useState(false);
  const [form,    setForm]    = useState(EMPTY_FORM);
  const [saving,  setSaving]  = useState(false);
  const [message, setMessage] = useState({ text: '', type: '' });

  const showMsg = (text, type = 'success') => {
    setMessage({ text, type });
    setTimeout(() => setMessage({ text: '', type: '' }), 3000);
  };

  const loadTypes = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetchAlertTypes();
      setTypes(res.data || []);
    } catch { showMsg('알림 유형 조회 실패', 'error'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { loadTypes(); }, [loadTypes]);

  const openNew = () => { setForm(EMPTY_FORM); setPopup(true); };
  const openEdit = (row) => {
    setForm({
      id:              row.id,
      type_name:       row.type_name,
      alert_category:  row.alert_category,
      alert_metric:    row.alert_metric,
      base_price_type: row.base_price_type,
      threshold_up:    row.threshold_up   ?? '',
      threshold_down:  row.threshold_down ?? '',
      cooldown_type:   row.cooldown_type,
      reset_hour:      row.reset_hour   ?? 9,
      reset_minute:    row.reset_minute ?? 0,
      is_active:       row.is_active,
    });
    setPopup(true);
  };

  const handleSave = async () => {
    if (!form.type_name.trim()) { alert('유형명을 입력하세요.'); return; }
    const cat = form.alert_category;
    if (cat !== 'DOWN' && form.threshold_up   === '') { alert('상승 기준값을 입력하세요.'); return; }
    if (cat !== 'UP'   && form.threshold_down === '') { alert('하락 기준값을 입력하세요.'); return; }

    const payload = {
      type_name:       form.type_name.trim(),
      alert_category:  form.alert_category,
      alert_metric:    form.alert_metric,
      base_price_type: form.base_price_type,
      threshold_up:    cat !== 'DOWN' ? parseFloat(form.threshold_up)   : null,
      threshold_down:  cat !== 'UP'   ? parseFloat(form.threshold_down) : null,
      cooldown_type:   form.cooldown_type,
      reset_hour:      form.cooldown_type === 'DAILY_TIME' ? parseInt(form.reset_hour)   || 9 : null,
      reset_minute:    form.cooldown_type === 'DAILY_TIME' ? parseInt(form.reset_minute) || 0 : null,
      is_active:       form.is_active,
    };
    setSaving(true);
    try {
      if (form.id) await updateAlertType(form.id, payload);
      else         await createAlertType(payload);
      setPopup(false);
      await loadTypes();
      showMsg('저장되었습니다.');
    } catch (e) {
      const detail = e.response?.data?.detail;
      const msg = Array.isArray(detail)
        ? detail.map(d => d.msg || JSON.stringify(d)).join(', ')
        : (typeof detail === 'string' ? detail : '저장 실패');
      alert(msg);
    }
    finally { setSaving(false); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('이 알림 유형을 삭제하시겠습니까?\n연결된 매수/매도 알림은 해제됩니다.')) return;
    try {
      await deleteAlertType(id);
      await loadTypes();
      showMsg('삭제되었습니다.');
    } catch { showMsg('삭제 실패', 'error'); }
  };

  const metricUnit = (v) => ({ PROFIT_RATE: '%', DIFF_RATE: '%', PRICE: '' })[v] || '';

  // ── 본문 컨텐츠 ────────────────────────────────────────────
  const content = (
    <div className={`atp-content${isEmbedded ? ' atp-embedded' : ''}`}>
      {/* 안내문 */}
      <div className="atp-info-box">
        <span className="atp-info-icon">ℹ️</span>
        <span>
          알림 유형은 매수/매도에 연결할 알림 템플릿입니다.
          미리 만들어두고 매수/매도 화면의 🔔 버튼으로 간편하게 연결하세요.
        </span>
      </div>

      {/* 툴바 */}
      <div className="atp-toolbar">
        <button className="btn btn-sm btn-primary" onClick={openNew}>+ 유형 추가</button>
      </div>

      {/* 메시지 */}
      {message.text && (
        <div className={`atp-msg atp-msg-${message.type}`}>{message.text}</div>
      )}

      {/* 그리드 */}
      <div className="atp-grid-wrap">
        <table className="grid-table atp-grid">
          <thead>
            <tr>
              <th>유형명</th>
              <th className="col-center">방향</th>
              <th className="col-center">기준</th>
              <th className="col-center">기준단가</th>
              <th className="col-right">상승값</th>
              <th className="col-right">하락값</th>
              <th className="col-center">재발송</th>
              <th className="col-center">리셋시각</th>
              <th className="col-center">활성</th>
              <th className="col-center">수정</th>
              <th className="col-center">삭제</th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr><td colSpan={11} className="grid-empty">조회중...</td></tr>
            )}
            {!loading && types.length === 0 && (
              <tr><td colSpan={11} className="grid-empty">등록된 알림 유형이 없습니다.</td></tr>
            )}
            {types.map(row => (
              <tr key={row.id}>
                <td>{row.type_name}</td>
                <td className="col-center">{catLabel(row.alert_category)}</td>
                <td className="col-center">{metLabel(row.alert_metric)}</td>
                <td className="col-center">
                  {row.base_price_type === 'AUTO' ? '자동' :
                   row.base_price_type === 'BUY_PRICE' ? '매수단가' : '매도단가'}
                </td>
                <td className="col-right atp-thresh-up">
                  {row.threshold_up != null
                    ? `${row.threshold_up}${metricUnit(row.alert_metric)}`
                    : '-'}
                </td>
                <td className="col-right atp-thresh-dn">
                  {row.threshold_down != null
                    ? `${row.threshold_down}${metricUnit(row.alert_metric)}`
                    : '-'}
                </td>
                <td className="col-center">{cdLabel(row.cooldown_type)}</td>
                <td className="col-center">
                  {row.cooldown_type === 'DAILY_TIME'
                    ? formatResetTime(row.reset_hour, row.reset_minute)
                    : '-'}
                </td>
                <td className="col-center">
                  <span className={`atp-badge ${row.is_active ? 'atp-badge-on' : 'atp-badge-off'}`}>
                    {row.is_active ? '활성' : '비활성'}
                  </span>
                </td>
                <td className="col-center">
                  <button className="btn btn-xs btn-outline" onClick={() => openEdit(row)}>수정</button>
                </td>
                <td className="col-center">
                  <button className="btn btn-xs btn-danger" onClick={() => handleDelete(row.id)}>삭제</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* ── 등록/수정 팝업 ── */}
      {popup && (
        <div className="ap-overlay">
          <div className="ap-popup atp-popup-wide">
            <div className="ap-popup-hd">
              {form.id ? '알림 유형 수정' : '알림 유형 등록'}
              <button className="ap-popup-close" onClick={() => setPopup(false)}>✕</button>
            </div>
            <div className="ap-popup-bd">

              <div className="ap-form-row">
                <label>유형명 *</label>
                <input className="ap-input" value={form.type_name}
                  onChange={e => setForm(p => ({ ...p, type_name: e.target.value }))}
                  placeholder="예: 수익률 5% 이상" />
              </div>

              <div className="ap-form-row">
                <label>알림 방향 *</label>
                <div className="ap-radio-group">
                  {CATEGORY_OPTIONS.map(o => (
                    <label key={o.value} className="ap-radio">
                      <input type="radio" checked={form.alert_category === o.value}
                        onChange={() => setForm(p => ({ ...p, alert_category: o.value }))} />
                      {o.label}
                    </label>
                  ))}
                </div>
              </div>

              <div className="ap-form-row">
                <label>알림 기준 *</label>
                <div className="ap-radio-group">
                  {METRIC_OPTIONS.map(o => (
                    <label key={o.value} className="ap-radio">
                      <input type="radio" checked={form.alert_metric === o.value}
                        onChange={() => setForm(p => ({ ...p, alert_metric: o.value }))} />
                      {o.label}
                    </label>
                  ))}
                </div>
              </div>

              <div className="ap-form-row">
                <label>기준단가</label>
                <select className="ap-select" value={form.base_price_type}
                  onChange={e => setForm(p => ({ ...p, base_price_type: e.target.value }))}>
                  {BASE_OPTIONS.map(o => (
                    <option key={o.value} value={o.value}>{o.label}</option>
                  ))}
                </select>
              </div>

              {form.alert_category !== 'DOWN' && (
                <div className="ap-form-row">
                  <label>상승 기준값 *</label>
                  <input className="ap-input ap-input-sm" type="number" step="any"
                    value={form.threshold_up}
                    onChange={e => setForm(p => ({ ...p, threshold_up: e.target.value }))}
                    placeholder="예: 5" />
                  <span className="ap-unit">{metricUnit(form.alert_metric)}</span>
                </div>
              )}

              {form.alert_category !== 'UP' && (
                <div className="ap-form-row">
                  <label>하락 기준값 *</label>
                  <input className="ap-input ap-input-sm" type="number" step="any" min="0"
                    value={form.threshold_down}
                    onChange={e => setForm(p => ({ ...p, threshold_down: e.target.value }))}
                    placeholder="예: 10" />
                  <span className="ap-unit">{metricUnit(form.alert_metric)}</span>
                  {form.alert_metric !== 'PRICE' && (
                    <span className="ap-hint">
                      양수로 입력 — 예) -10% 이하 알림 → 10 입력
                    </span>
                  )}
                </div>
              )}

              <div className="ap-form-row">
                <label>재발송 방식</label>
                <select className="ap-select" value={form.cooldown_type}
                  onChange={e => setForm(p => ({ ...p, cooldown_type: e.target.value }))}>
                  {COOLDOWN_OPTIONS.map(o => (
                    <option key={o.value} value={o.value}>{o.label}</option>
                  ))}
                </select>
              </div>

              {form.cooldown_type === 'DAILY_TIME' && (
                <div className="ap-form-row">
                  <label>재발송 가능 시각</label>
                  <input className="ap-input ap-input-xs" type="number" min={0} max={23}
                    value={form.reset_hour}
                    onChange={e => setForm(p => ({ ...p, reset_hour: e.target.value }))} />
                  <span className="ap-unit">시</span>
                  <select className="ap-select ap-select-xs"
                    value={form.reset_minute}
                    onChange={e => setForm(p => ({ ...p, reset_minute: parseInt(e.target.value) }))}>
                    {MINUTE_OPTIONS.map(m => (
                      <option key={m} value={m}>{String(m).padStart(2, '0')}</option>
                    ))}
                  </select>
                  <span className="ap-unit">분 (다음날 이 시각 이후 재발송)</span>
                </div>
              )}

              <div className="ap-form-row">
                <label>활성 여부</label>
                <label className="ap-radio">
                  <input type="checkbox" checked={form.is_active}
                    onChange={e => setForm(p => ({ ...p, is_active: e.target.checked }))} />
                  활성
                </label>
              </div>

            </div>
            <div className="ap-popup-ft">
              <button className="btn btn-sm btn-outline" onClick={() => setPopup(false)}>취소</button>
              <button className="btn btn-sm btn-primary" onClick={handleSave} disabled={saving}>
                {saving ? '저장중...' : '저장'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );

  // ── 렌더링 모드 결정 ─────────────────────────────────────
  if (isEmbedded) {
    return content;
  }

  if (isModal) {
    return (
      <div className="ap-modal-overlay">
        <div className="ap-modal-box">
          <div className="ap-modal-hd">
            알림 유형 관리
            <button className="ap-modal-close" onClick={onClose}>✕</button>
          </div>
          <div className="atp-modal-body">
            {content}
          </div>
        </div>
      </div>
    );
  }

  return (
    <PageLayout title="알림 유형 관리">
      {content}
    </PageLayout>
  );
};

export default AlertTypePage;
